import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAPIVisitorPasses } from 'services/visitor-passes/api-visitor-passes.service';
import { useAPIAnnouncements } from 'services/announcements/api-announcements.service';
import { CreateVisitorPassResponse } from 'services/visitor-passes/visitor-passes.service';
import { onSharePasscode } from 'mobile/passcodes/passcode-functions';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { deviceOffset } from 'tools/date/site-time';
import { AdminSiteOption, ValidationError, VisitorPassCreationController } from './interfaces';
import { computeWindow, EndPreset, isWindowInFuture, isWindowValid, StartMode } from './pass-window';
import { buildSummary } from './summary-label';

export const useVisitorPassCreationController = (
    service = useAPIVisitorPasses(),
    sitesService = useAPIAnnouncements(),
): VisitorPassCreationController => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { translate } = useTranslator();
    const accessToken = searchParams.get('access_token') || '';

    const [step, setStep] = useState<0 | 1>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasNetworkError, setHasNetworkError] = useState(false);
    const [adminSites, setAdminSites] = useState<AdminSiteOption[]>([]);
    const [sitesLoaded, setSitesLoaded] = useState(false);
    // Causa distinta de hasNetworkError (que es del submit): getAdminSites es un
    // dato requerido acá (a diferencia del chooser del "+", que lo tolera en
    // silencio), así que su fallo necesita su propio estado, copy y reintento.
    const [sitesLoadError, setSitesLoadError] = useState(false);
    const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>();
    const [visitorName, setVisitorName] = useState('');
    const [panelOpen, setPanelOpen] = useState(false);
    // El sitio no es un parámetro de la vigencia: tiene su propio selector,
    // independiente del panel de "Cambiar" (empieza/vence/usos).
    const [sitePickerOpen, setSitePickerOpen] = useState(false);
    const [startMode, setStartMode] = useState<StartMode>('now');
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endPreset, setEndPreset] = useState<EndPreset>('p1');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');
    const [maxUses, setMaxUses] = useState<number | undefined>(undefined);
    const [created, setCreated] = useState<CreateVisitorPassResponse | undefined>();
    // Nada en esta pantalla dispara un re-render sólo porque pasó el tiempo.
    // submit() la usa para forzar uno cuando encuentra la ventana vencida, así
    // validationError (recalculado en cada render, no memoizado) refleja
    // 'range' y el usuario ve el mismo error que vería si esto hubiera
    // aparecido durante el render, en vez de un fallo mudo.
    const [, forceRerender] = useState(0);

    // Reinvocable a propósito: es el punto de reintento cuando getAdminSites
    // falla por red (Task del gate final). Sin esto, un hipo de conexión deja
    // sitesLoaded en false para siempre y ninguna acción en pantalla lo revierte.
    const loadSites = () => {
        setSitesLoadError(false);
        sitesService
            .getAdminSites(accessToken)
            .then((sites) => {
                const opts = sites.map((s) => ({
                    siteId: s.siteId,
                    siteName: s.siteName,
                    timezone: s.timezone,
                }));
                setAdminSites(opts);
                setSitesLoaded(true);
                if (opts.length === 1) {
                    setSelectedSiteId(opts[0].siteId);
                }
            })
            .catch(() => setSitesLoadError(true));
    };

    useEffect(() => {
        if (!accessToken) {
            navigate('/mobile/404');
            return;
        }
        loadSites();
    }, []);

    const site = useMemo(() => adminSites.find((s) => s.siteId === selectedSiteId), [adminSites, selectedSiteId]);

    // El offset del sitio manda; sin él, el del dispositivo. Degradar al reloj
    // local es exactamente el comportamiento anterior al feature.
    const offset = site?.timezone || deviceOffset();

    const windowInput = useMemo(
        () => ({ startMode, startDate, startTime, endPreset, endDate, endTime, offset }),
        [startMode, startDate, startTime, endPreset, endDate, endTime, offset],
    );

    // Se recalcula en cada render a propósito: "hoy" tiene que seguir siendo hoy
    // aunque la pantalla quede abierta cruzando la medianoche.
    const passWindow = computeWindow(windowInput);

    const isExactComplete =
        (startMode !== 'exact' || (!!startDate && !!startTime)) && (endPreset !== 'exact' || (!!endDate && !!endTime));

    // Sin useMemo a propósito, igual que passWindow: memoizar sobre
    // passWindow.nbf/exp ignora que isWindowInFuture compara contra "ahora", y
    // "ahora" cambia sin que ninguna de esas dependencias cambie. Memoizado,
    // este chequeo quedaba congelado en el resultado del primer render.
    const validationError: ValidationError | undefined = (() => {
        if (!selectedSiteId) {
            return 'site';
        }
        if (!visitorName.trim()) {
            return 'name';
        }
        if (!isExactComplete) {
            return 'incomplete';
        }
        if (!isWindowValid(passWindow) || !isWindowInFuture(passWindow)) {
            return 'range';
        }
        return undefined;
    })();

    const canSubmit = !validationError && !isLoading;

    // Con fecha exacta a medio elegir, passWindow trae dayjs('') (Invalid Date):
    // buildSummary armaría "undefined Invalid Date" sobre datos que todavía no
    // existen. isExactComplete ya distingue este estado (es la misma condición
    // que decide validationError === 'incomplete'), así que se corta acá antes
    // de construir el resumen con basura.
    const validityLabel = useMemo(
        () =>
            isExactComplete
                ? buildSummary({
                      window: passWindow,
                      startMode,
                      endPreset,
                      startTime,
                      endTime,
                      maxUses,
                      translate,
                  })
                : translate({ key: 'visit-pass.summary-incomplete' }),
        [
            isExactComplete,
            passWindow.startDate,
            passWindow.endDate,
            startMode,
            endPreset,
            startTime,
            endTime,
            maxUses,
            translate,
        ],
    );

    const showTimezoneNote = !!site?.timezone && site.timezone !== deviceOffset();

    const submit = () => {
        if (!canSubmit || !selectedSiteId) {
            return;
        }
        // Se recalcula acá y no al montar: entre que se abrió la pantalla y se
        // tocó el botón puede haber pasado una hora, o haber cambiado el día.
        const w = computeWindow(windowInput);
        // canSubmit se leyó del último render, que puede haber quedado viejo:
        // nada fuerza un re-render sólo porque pasó el tiempo. Revalidar acá
        // evita mandar al backend un pase que ya nació vencido — el backend
        // sólo valida exp > nbf, no exp > ahora.
        if (!isWindowValid(w) || !isWindowInFuture(w)) {
            forceRerender((n) => n + 1);
            return;
        }
        setIsLoading(true);
        setHasNetworkError(false);
        return service
            .createVisitorPass(accessToken, {
                site_id: selectedSiteId,
                visitor_name: visitorName.trim(),
                nbf: w.nbf,
                exp: w.exp,
                max_uses: maxUses,
            })
            .then((r) => {
                setCreated(r);
                setStep(1);
            })
            .catch(() => setHasNetworkError(true))
            .finally(() => setIsLoading(false));
    };

    const onShare = () => {
        if (!created) {
            return;
        }
        onSharePasscode({
            v: 1,
            data: { message: `Pase de visita para ${visitorName.trim()}: ${created.share_url}` },
        });
    };

    const reset = () => {
        setStep(0);
        setCreated(undefined);
        setVisitorName('');
        setPanelOpen(false);
        setSitePickerOpen(false);
        setStartMode('now');
        setStartDate('');
        setStartTime('');
        setEndPreset('p1');
        setEndDate('');
        setEndTime('');
        setMaxUses(undefined);
    };

    return {
        step,
        isLoading,
        hasNetworkError,
        adminSites,
        sitesLoaded,
        sitesLoadError,
        selectedSiteId,
        visitorName,
        panelOpen,
        sitePickerOpen,
        startMode,
        startDate,
        startTime,
        endPreset,
        endDate,
        endTime,
        maxUses,
        created,
        canSubmit,
        validationError,
        validityLabel,
        showTimezoneNote,
        setVisitorName,
        setSelectedSiteId,
        togglePanel: () => setPanelOpen((v) => !v),
        toggleSitePicker: () => setSitePickerOpen((v) => !v),
        setStartMode,
        setStartDate,
        setStartTime,
        setEndPreset,
        setEndDate,
        setEndTime,
        setMaxUses,
        submit,
        onShare,
        reset,
        retryLoadSites: loadSites,
    };
};
